import {useContext} from 'react'

import PropTypes from 'prop-types'
import md5 from 'tiny-js-md5'

import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'

const SET_ACCOUNT_EVENT_NAME = 'setAccount'
const SET_SITE_TYPE_EVENT_NAME = 'setSiteType'
const SET_CUSTOMER_ID_EVENT_NAME = 'setCustomerId'
const SET_EMAIL_EVENT_NAME = 'setEmail'
const SET_HASHED_EMAIL_EVENT_NAME = 'setHashedEmail'

const DEFAULT_PAGE_NAME = 'home'

const HOME_PAGE_EVENT_NAME = 'viewHome'
const ADS_LIST_PAGE_EVENT_NAME = 'viewList'
const ADS_DETAIL_PAGE_EVENT_NAME = 'viewItem'

const SITE_TYPE_MOBILE = 'm'
const SITE_TYPE_DESKTOP = 'd'

const TrackingCriteo = ({eventData = [], pageName = DEFAULT_PAGE_NAME}) => {
  const {domain} = useContext(Context)
  const {user} = useContext(AuthenticationContext)
  const config = domain.get('config')
  const isMobile = config.get('isMobile')
  const {CRITEO_ACCOUNT_ID} = config.get('tracking')
  const hasEventData = Boolean(eventData.length)
  const {
    HOME_PAGE_NAME,
    ADS_LIST_PAGE_NAME,
    ADS_LIST_BUGGIES_PAGE_NAME,
    ADS_LIST_CLASSIC_AND_COMPETITION_PAGE_NAME,
    ADS_LIST_MINIMOTOS_PAGE_NAME,
    ADS_LIST_QUADS_PAGE_NAME,
    ADS_LIST_NEW_PAGE_NAME,
    ADS_LIST_BUGGIES_NEW_PAGE_NAME,
    ADS_LIST_MINIMOTOS_NEW_PAGE_NAME,
    ADS_LIST_QUADS_NEW_PAGE_NAME,
    ADS_LIST_VANS_AND_COMMERCIAL_PAGE_NAME,
    ADS_LIST_AUTOCARAVANS_AND_TRAILERS_PAGE_NAME,
    ADS_LIST_CARS_WITHOUT_LICENSE_PAGE_NAME,
    ADS_DETAIL_PAGE_NAME
  } = config.get('page')

  const getCriteoData = () => {
    const {userId = '', email = ''} = user || {}

    const getPageEventNameFromPageName = ({pageName}) => {
      switch (pageName) {
        case HOME_PAGE_NAME:
          return HOME_PAGE_EVENT_NAME
        case ADS_LIST_PAGE_NAME:
        case ADS_LIST_BUGGIES_PAGE_NAME:
        case ADS_LIST_MINIMOTOS_PAGE_NAME:
        case ADS_LIST_QUADS_PAGE_NAME:
        case ADS_LIST_NEW_PAGE_NAME:
        case ADS_LIST_BUGGIES_NEW_PAGE_NAME:
        case ADS_LIST_MINIMOTOS_NEW_PAGE_NAME:
        case ADS_LIST_QUADS_NEW_PAGE_NAME:
        case ADS_LIST_VANS_AND_COMMERCIAL_PAGE_NAME:
        case ADS_LIST_CLASSIC_AND_COMPETITION_PAGE_NAME:
        case ADS_LIST_AUTOCARAVANS_AND_TRAILERS_PAGE_NAME:
        case ADS_LIST_CARS_WITHOUT_LICENSE_PAGE_NAME:
          return ADS_LIST_PAGE_EVENT_NAME
        case ADS_DETAIL_PAGE_NAME:
          return ADS_DETAIL_PAGE_EVENT_NAME
        default:
          return HOME_PAGE_EVENT_NAME
      }
    }

    const pageEventName = getPageEventNameFromPageName({pageName})

    return {
      accountIds: CRITEO_ACCOUNT_ID,
      customerId: String(userId),
      siteType: isMobile ? SITE_TYPE_MOBILE : SITE_TYPE_DESKTOP,
      pageEvent: {event: pageEventName, ...(hasEventData && {item: eventData})},
      hashedEmail: email ? md5(email) : email
    }
  }

  const setCriteoData = ({accountIds, customerId, siteType, email, hashedEmail, pageEvent}) => {
    const criteoData = [
      {event: SET_ACCOUNT_EVENT_NAME, account: accountIds},
      {event: SET_SITE_TYPE_EVENT_NAME, type: siteType},
      customerId && {event: SET_CUSTOMER_ID_EVENT_NAME, id: customerId},
      !hashedEmail && {event: SET_EMAIL_EVENT_NAME, email},
      hashedEmail && {event: SET_HASHED_EMAIL_EVENT_NAME, email: hashedEmail},
      pageEvent
    ].filter(Boolean)

    // Push events to criteo
    window.criteo_q = window.criteo_q || []
    window.criteo_q.push(...criteoData)
  }

  useMount(() => {
    const criteoData = getCriteoData()

    setCriteoData(criteoData)
  })

  return <></>
}

TrackingCriteo.displayName = 'TrackingCriteo'

TrackingCriteo.propTypes = {
  eventData: PropTypes.array,
  pageName: PropTypes.string
}

export default TrackingCriteo
